class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.onRebuyAdd = this.onRebuyAdd.bind(this);
    this.onRebuyCartAdd = this.onRebuyCartAdd.bind(this);
    this.lastRebuyAddAt = 0;
  }

  connectedCallback() {
    document.addEventListener('rebuy.add', this.onRebuyAdd);
    document.addEventListener('rebuy:cart.add', this.onRebuyCartAdd);
  }

  disconnectedCallback() {
    document.removeEventListener('rebuy.add', this.onRebuyAdd);
    document.removeEventListener('rebuy:cart.add', this.onRebuyCartAdd);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    return (async (triggeredByArg) => {
      const triggered = triggeredByArg || triggeredBy;
      if (triggered) this.setActiveElement(triggered);
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

      try {
        // Refresh contents from server to ensure totals/discounts are up-to-date before opening
        await this.refreshContents();
      } catch (e) {
        // ignore refresh failures and open anyway
      }

      // here the animation doesn't seem to always get triggered. a timeout seems to help
      setTimeout(() => {
        this.classList.add('animate', 'active');
      });

      this.addEventListener(
        'transitionend',
        () => {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          trapFocus(containerToTrapFocusOn, focusElement);
        },
        { once: true }
      );

      document.body.classList.add('overflow-hidden');
    })(null);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove(
      'smart-cart--enabled',
      'rebuy-modal-visible',
      'rebuy-cart-visible'
    );    
  }

  onRebuyAdd() {
    this.lastRebuyAddAt = Date.now();
    this.refreshFromRebuy({ open: true });
  }

  onRebuyCartAdd() {
    if (Date.now() - this.lastRebuyAddAt < 500) return;
    this.refreshFromRebuy({ open: true });
  }

  async refreshFromRebuy({ open = false } = {}) {
    try {
      await this.refreshContents();
      if (open) {
        // open without triggering an additional refresh
        this.open();
      }
    } catch (e) {
      console.error(e);
    }
  }

  async refreshContents() {
    try {
      const [drawerResponse, bubbleResponse] = await Promise.all([
        fetch(`${routes.cart_url}?section_id=cart-drawer`),
        fetch(`${routes.cart_url}?section_id=cart-icon-bubble`),
      ]);

      const [drawerText, bubbleText] = await Promise.all([drawerResponse.text(), bubbleResponse.text()]);

      const drawerHtml = new DOMParser().parseFromString(drawerText, 'text/html');
      const bubbleHtml = new DOMParser().parseFromString(bubbleText, 'text/html');

      const selectors = ['cart-drawer-items', '#rebuy-cart-recommendations', '.cart-drawer__footer'];
      for (const selector of selectors) {
        const targetElement = document.querySelector(selector);
        const sourceElement = drawerHtml.querySelector(selector);
        if (targetElement && sourceElement) {
          targetElement.replaceWith(sourceElement);
        }
      }

      const currentBubble = document.getElementById('cart-icon-bubble');
      const nextBubble = bubbleHtml.getElementById('cart-icon-bubble');
      if (currentBubble && nextBubble) {
        currentBubble.innerHTML = nextBubble.innerHTML;
      }

      const nextDrawer = drawerHtml.querySelector('cart-drawer');
      if (nextDrawer) {
        this.classList.toggle('is-empty', nextDrawer.classList.contains('is-empty'));
      }

      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));

      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (e) {
      throw e;
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);
if (typeof CartItems !== 'undefined') {
class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
}
// Enable touchpad (trackpad) horizontal swipe support for Rebuy's Flickity slider
(function () {
  let deltaAccum = 0;
  let cooldown = false;

  document.addEventListener('wheel', (e) => {
    const rebuyContainer = document.getElementById('rebuy-cart-recommendations');
    if (!rebuyContainer) return;

    // Check cursor is over the rebuy container using coordinates
    const rect = rebuyContainer.getBoundingClientRect();
    if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) return;

    // Only act when horizontal scroll dominates
    if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) return;
    e.preventDefault();

    if (cooldown) return;

    deltaAccum += e.deltaX;

    if (deltaAccum > 30) {
      const btn = rebuyContainer.querySelector('.flickity-button.next');
      if (btn) btn.click();
      deltaAccum = 0;
      cooldown = true;
      setTimeout(() => { cooldown = false; }, 500);
    } else if (deltaAccum < -30) {
      const btn = rebuyContainer.querySelector('.flickity-button.previous');
      if (btn) btn.click();
      deltaAccum = 0;
      cooldown = true;
      setTimeout(() => { cooldown = false; }, 500);
    }
  }, { passive: false });
})();
